import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { Box, Stack } from '@mui/material';
import { colors, Typography, Tabs } from '@sweep-io/sweep-design';
import { SortWidget } from '../../common/SortWidget';
import { ArtifactCardGrid } from './ArtifactCardGrid';
import { ArtifactsEmptyState } from './ArtifactsEmptyState';
import { Artifact } from '../../../types/artifactsTypes';
import { CenteredCircularProgress } from '../../common/CenteredCircularProgress';
import { SearchInput } from '../../common/SearchInput';
import { AdvancedFilter } from '../../common/filters/advanced-filter/AdvancedFilter';
import { DatePickerWrapper } from '../../common/datepicker/DatePickerWrapper';
import { SingleSelectFilter } from '../../common/filters/single-select-filter/SingleSelectFilter';
import { CustomDatePicker, DateBy } from '../../common/datepicker/types';
import { useDispatch, useSelector } from 'react-redux';
import { selectAccountUsers } from '../../../reducers/accountUsersReducer';
import { selectUserInfoData } from '../../../reducers/userInfoReducer';
import keyBy from 'lodash/keyBy';
import { useAiAgentApiFacade } from '../../../apis/facades/useAiAgentApiFacade';
import { telemetry } from '../../../telemetry';
import {
  setArtifactsViewActive,
  setPendingArtifactToOpen,
  setAdvancedMode,
} from '../../aiAgentCenter/agentsHomeReducer';
import { AgentInputMode } from '../../aiAgentCenter/agent-input/agentInputModeTypes';
import { useArtifacts } from '../../common/artifacts/useArtifacts';
import useAgentsChats from '../../aiAgentCenter/useAgentsChats';
import { AiAgent } from '../../aiAgentCenter/aiAgentsConsts';
import { getContentType } from '../../helpers/artifactHelpers';
import { useFeatureToggle } from '../../../feature-flags/useFeatureToggle';
import { BuiltBySweepTab } from './templates/BuiltBySweepTab';

// Visibility filter options for Shared tab (single select)
enum SharedVisibilityFilter {
  All = 'all',
  Published = 'published',
  NotPublished = 'not_published',
}

const sharedVisibilityOptions = [
  { label: 'All', value: SharedVisibilityFilter.All },
  { label: 'Published', value: SharedVisibilityFilter.Published },
  { label: 'Not published', value: SharedVisibilityFilter.NotPublished },
];

// Visibility filter options for My Artifacts tab (multi select)
enum MyVisibilityFilter {
  Published = 'published',
  Shared = 'shared',
  Private = 'private',
}

const myVisibilityFilterItems = [
  { label: 'Published', value: MyVisibilityFilter.Published },
  { label: 'Shared', value: MyVisibilityFilter.Shared },
  { label: 'Private', value: MyVisibilityFilter.Private },
];

// Sort options
enum SortOption {
  LastCreated = 'last_created',
  AToZ = 'a_to_z',
  ZToA = 'z_to_a',
}

const sortOptions = [
  { label: 'Last Created', value: SortOption.LastCreated },
  { label: 'A - Z', value: SortOption.AToZ },
  { label: 'Z - A', value: SortOption.ZToA },
];

export type ArtifactsTab = 'built-by-sweep' | 'shared' | 'my-artifacts';

export const ARTIFACTS_TABS: Record<string, ArtifactsTab> = {
  BUILT_BY_SWEEP: 'built-by-sweep',
  SHARED: 'shared',
  MY_ARTIFACTS: 'my-artifacts',
};

interface ArtifactsPageProps {
  activeAgent: AiAgent;
}

export const ArtifactsPage = ({ activeAgent }: ArtifactsPageProps) => {
  const { artifactTemplates: isArtifactTemplatesEnabled } = useFeatureToggle();
  const [artifacts, setArtifacts] = useState<Artifact[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { get_artifacts, get_artifact } = useAiAgentApiFacade();
  const userInfo = useSelector(selectUserInfoData);
  const currentUserId = userInfo?.id;
  const dispatch = useDispatch();
  const { setAgentClickHandler, setActiveChatClickHandler, navigateToAgentOrChat } =
    useAgentsChats();
  const { artifacts: artifactsContext } = useArtifacts();
  const agentId = activeAgent.id;
  // Default to 'built-by-sweep' if feature flag is enabled, otherwise 'shared'
  const [activeTab, setActiveTab] = useState<ArtifactsTab>(
    isArtifactTemplatesEnabled ? ARTIFACTS_TABS.BUILT_BY_SWEEP : ARTIFACTS_TABS.SHARED,
  );
  const [searchQuery, setSearchQuery] = useState('');
  // Shared tab filters
  const [selectedAuthors, setSelectedAuthors] = useState<string[]>([]);
  const [sharedDateFilter, setSharedDateFilter] = useState<CustomDatePicker | null>(null);
  const [sharedVisibilityFilter, setSharedVisibilityFilter] = useState<SharedVisibilityFilter>(
    SharedVisibilityFilter.All,
  );
  const [sharedSortBy, setSharedSortBy] = useState<SortOption>(SortOption.LastCreated);
  // My Artifacts tab filters
  const [myDateFilter, setMyDateFilter] = useState<CustomDatePicker | null>(null);
  const [myVisibilityFilter, setMyVisibilityFilter] = useState<MyVisibilityFilter[]>([]);
  const [mySortBy, setMySortBy] = useState<SortOption>(SortOption.LastCreated);

  // User lookup for name resolution
  const accountUsers = useSelector(selectAccountUsers);
  const usersById = useMemo(() => keyBy(accountUsers, 'id'), [accountUsers]);

  // Clear chatId from URL when entering artifacts page (only once on mount)
  // This allows useAgentRouteSync to create a fresh ghost chat
  const hasResetUrl = useRef(false);
  useEffect(() => {
    if (!hasResetUrl.current) {
      hasResetUrl.current = true;
      navigateToAgentOrChat({ agentId: activeAgent.id });
    }
  }, [navigateToAgentOrChat, activeAgent.id]);

  // Fetch artifacts from API
  const fetchArtifacts = useCallback(async () => {
    if (!agentId) return;
    setIsLoading(true);
    try {
      const data = await get_artifacts(agentId);
      setArtifacts(data);
    } catch (error) {
      telemetry.captureError(error);
    } finally {
      setIsLoading(false);
    }
  }, [agentId, get_artifacts]);

  useEffect(() => {
    fetchArtifacts();
  }, [fetchArtifacts]);

  // Separate shared vs my artifacts
  const sharedArtifacts = useMemo(() => artifacts.filter((a) => a.isShared), [artifacts]);
  const myArtifacts = useMemo(
    () => artifacts.filter((a) => a.createdById === currentUserId),
    [artifacts, currentUserId],
  );

  // Author filter options - only show authors who have shared artifacts
  const authorFilterItems = useMemo(() => {
    const authorIds = new Set(sharedArtifacts.map((a) => a.createdById));

    return (accountUsers || [])
      .filter((user) => user.id && authorIds.has(user.id))
      .map((user) => ({
        label: user.name || user.email || 'Unknown',
        value: user.id as string,
      }));
  }, [accountUsers, sharedArtifacts]);

  const filteredArtifacts = useMemo(() => {
    const baseArtifacts = activeTab === ARTIFACTS_TABS.SHARED ? sharedArtifacts : myArtifacts;

    // Filter
    const filtered = baseArtifacts.filter((artifact) => {
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesTitle = artifact.title.toLowerCase().includes(query);
        const createdByName = usersById[artifact.createdById]?.name || '';
        const matchesAuthor = createdByName.toLowerCase().includes(query);
        if (!matchesTitle && !matchesAuthor) return false;
      }

      if (activeTab === ARTIFACTS_TABS.SHARED) {
        // Author filter
        if (selectedAuthors.length > 0) {
          if (!selectedAuthors.includes(artifact.createdById)) return false;
        }

        // Date filter
        if (sharedDateFilter) {
          const artifactDate = new Date(artifact.createdAt);
          if (sharedDateFilter[DateBy.From]) {
            const fromDate = new Date(sharedDateFilter[DateBy.From]);
            if (artifactDate < fromDate) return false;
          }
          if (sharedDateFilter[DateBy.To]) {
            const toDate = new Date(sharedDateFilter[DateBy.To]);
            if (artifactDate > toDate) return false;
          }
        }

        // Visibility filter
        if (sharedVisibilityFilter === SharedVisibilityFilter.Published) {
          if (!artifact.isPublic) return false;
        } else if (sharedVisibilityFilter === SharedVisibilityFilter.NotPublished) {
          if (artifact.isPublic) return false;
        }
      } else {
        // My Artifacts tab filters

        // Date filter
        if (myDateFilter) {
          const artifactDate = new Date(artifact.createdAt);
          if (myDateFilter[DateBy.From]) {
            const fromDate = new Date(myDateFilter[DateBy.From]);
            if (artifactDate < fromDate) return false;
          }
          if (myDateFilter[DateBy.To]) {
            const toDate = new Date(myDateFilter[DateBy.To]);
            if (artifactDate > toDate) return false;
          }
        }

        // Visibility filter (multi-select, OR logic)
        if (myVisibilityFilter.length > 0) {
          const matchesPublished =
            myVisibilityFilter.includes(MyVisibilityFilter.Published) && artifact.isPublic;
          const matchesShared =
            myVisibilityFilter.includes(MyVisibilityFilter.Shared) && artifact.isShared;
          const matchesPrivate =
            myVisibilityFilter.includes(MyVisibilityFilter.Private) &&
            !artifact.isShared &&
            !artifact.isPublic;

          if (!matchesPublished && !matchesShared && !matchesPrivate) return false;
        }
      }

      return true;
    });

    // Sort
    const sortBy = activeTab === ARTIFACTS_TABS.SHARED ? sharedSortBy : mySortBy;
    return [...filtered].sort((a, b) => {
      switch (sortBy) {
        case SortOption.AToZ:
          return a.title.localeCompare(b.title);
        case SortOption.ZToA:
          return b.title.localeCompare(a.title);
        case SortOption.LastCreated:
        default:
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      }
    });
  }, [
    activeTab,
    sharedArtifacts,
    myArtifacts,
    searchQuery,
    selectedAuthors,
    sharedDateFilter,
    sharedVisibilityFilter,
    sharedSortBy,
    myDateFilter,
    myVisibilityFilter,
    mySortBy,
    usersById,
  ]);

  // Check if any filters are active (excluding default values)
  const isFiltered = useMemo(() => {
    if (searchQuery) return true;

    if (activeTab === ARTIFACTS_TABS.SHARED) {
      return (
        selectedAuthors.length > 0 ||
        sharedDateFilter !== null ||
        sharedVisibilityFilter !== SharedVisibilityFilter.All
      );
    } else {
      return myDateFilter !== null || myVisibilityFilter.length > 0;
    }
  }, [
    searchQuery,
    activeTab,
    selectedAuthors,
    sharedDateFilter,
    sharedVisibilityFilter,
    myDateFilter,
    myVisibilityFilter,
  ]);

  const handleCardClick = useCallback(
    async (artifact: Artifact) => {
      if (activeTab === ARTIFACTS_TABS.SHARED) {
        // Open shared artifact in new tab
        const sharedUrl = `${window.location.origin}/share-artifacts/${artifact.id}`;
        window.open(sharedUrl, '_blank');
        return;
      }

      // "My Artifacts" tab - Navigate to chat with artifact panel open
      if (!artifact.chatId) {
        return;
      }

      // Fetch artifact content first
      let artifactContent: string | null = null;
      let detectedContentType: ReturnType<typeof getContentType> | null = null;
      let fetchedArtifactData: Awaited<ReturnType<typeof get_artifact>> | null = null;

      // Check if artifact already has content in context
      const existingArtifact = artifactsContext[artifact.artifactId];

      if (existingArtifact?.versions[0]?.content) {
        // Artifact already exists in context - use existing data
        const existingVersion = existingArtifact.versions.find((v) => v.versionId === artifact.id);
        if (existingVersion) {
          dispatch(
            setPendingArtifactToOpen({
              artifactId: artifact.artifactId,
              versionId: artifact.id,
              targetChatId: artifact.chatId,
              artifactData: existingArtifact,
            }),
          );
        }
      } else {
        // Fetch artifact content before navigating
        try {
          fetchedArtifactData = await get_artifact(artifact.id, { checkSharingAccess: true });
          artifactContent = fetchedArtifactData.code;
          detectedContentType = getContentType(artifactContent);
        } catch (error) {
          telemetry.captureError(error);
          return;
        }

        // Prepare artifact data for Redux
        if (fetchedArtifactData && artifactContent && detectedContentType) {
          dispatch(
            setPendingArtifactToOpen({
              artifactId: artifact.artifactId,
              versionId: artifact.id,
              targetChatId: artifact.chatId,
              artifactData: {
                artifactId: artifact.artifactId,
                versions: [
                  {
                    versionId: artifact.id,
                    title: artifact.title,
                    content: artifactContent,
                    contentType: detectedContentType,
                    createdAt: fetchedArtifactData.createdAt,
                    version: fetchedArtifactData.version,
                  },
                ],
                currentVersion: artifact.id,
                isShared: fetchedArtifactData.isShared,
                isPublic: fetchedArtifactData.isPublic,
              },
            }),
          );
        }
      }

      // Navigate to chat (also closes artifacts view)
      setActiveChatClickHandler(artifact.chatId);
    },
    [activeTab, dispatch, artifactsContext, get_artifact, setActiveChatClickHandler],
  );

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: 8, pt: 4 }}>
      <Stack gap={2} mb={4}>
        <Typography variant="h2-medium">Artifacts</Typography>
        <Typography variant="h4" color={colors.grey[700]}>
          {isArtifactTemplatesEnabled
            ? 'Access your work, discover team insights, and explore curated templates'
            : 'Access your artifacts and discover what your team has shared'}
        </Typography>
      </Stack>

      {/* Tabs */}
      <Box
        sx={{
          '.MuiTabs-root': {
            borderBottom: `1px solid ${colors.grey[120]}`,
          },
          mb: 3,
        }}
      >
        <Tabs
          value={activeTab}
          onChange={(_, value) => setActiveTab(value as ArtifactsTab)}
          tabs={[
            ...(isArtifactTemplatesEnabled
              ? [{ value: ARTIFACTS_TABS.BUILT_BY_SWEEP, label: 'Built by Sweep' }]
              : []),
            { value: ARTIFACTS_TABS.SHARED, label: 'Shared Artifacts' },
            { value: ARTIFACTS_TABS.MY_ARTIFACTS, label: 'My Artifacts' },
          ]}
        />
      </Box>

      {/* Built by Sweep Tab Content */}
      {activeTab === ARTIFACTS_TABS.BUILT_BY_SWEEP && <BuiltBySweepTab activeAgent={activeAgent} />}

      {/* Shared and My Artifacts Tab Content */}
      {(activeTab === ARTIFACTS_TABS.SHARED || activeTab === ARTIFACTS_TABS.MY_ARTIFACTS) && (
        <>
          {/* Filters and Search */}
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
            <Stack direction="row" gap={2} alignItems="center">
              <Typography variant="body" color={colors.grey[700]}>
                Filters:
              </Typography>
              {activeTab === ARTIFACTS_TABS.SHARED ? (
                <>
                  <AdvancedFilter
                    items={authorFilterItems}
                    selectedItems={selectedAuthors}
                    onSelectedItemsChange={setSelectedAuthors}
                    texts={{ title: 'Author' }}
                  />
                  <DatePickerWrapper
                    onSelectDate={setSharedDateFilter}
                    placeholder="Creation date"
                    showClearButton
                  />
                  <SingleSelectFilter
                    options={sharedVisibilityOptions}
                    selectedItemValue={sharedVisibilityFilter}
                    onSelectItem={(value) =>
                      setSharedVisibilityFilter(value as SharedVisibilityFilter)
                    }
                    filterTitleText="Visibility"
                  />
                </>
              ) : (
                <>
                  <DatePickerWrapper
                    onSelectDate={setMyDateFilter}
                    placeholder="Creation date"
                    showClearButton
                  />
                  <AdvancedFilter
                    items={myVisibilityFilterItems}
                    selectedItems={myVisibilityFilter}
                    onSelectedItemsChange={(items) =>
                      setMyVisibilityFilter(items as MyVisibilityFilter[])
                    }
                    texts={{ title: 'Visibility' }}
                  />
                </>
              )}
            </Stack>

            {/* Sort and Search */}
            <Stack direction="row" gap={2} alignItems="center">
              <SortWidget
                value={activeTab === ARTIFACTS_TABS.SHARED ? sharedSortBy : mySortBy}
                onChange={(value) =>
                  activeTab === ARTIFACTS_TABS.SHARED
                    ? setSharedSortBy(value as SortOption)
                    : setMySortBy(value as SortOption)
                }
                options={sortOptions}
              />
              <Box sx={{ width: '240px' }}>
                <SearchInput
                  withFixedMagnifyingGlassIcon
                  placeholder="Search"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onClearButtonClick={() => setSearchQuery('')}
                  size="default"
                />
              </Box>
            </Stack>
          </Stack>

          {/* Artifact count */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="body" color={colors.grey[700]}>
              {isLoading ? 'Loading...' : `Showing ${filteredArtifacts.length} Artifacts`}
            </Typography>
          </Box>

          {/* Content */}
          <Box sx={{ flex: 1, overflow: 'auto' }}>
            {isLoading && <CenteredCircularProgress />}

            {!isLoading && filteredArtifacts.length === 0 && (
              <ArtifactsEmptyState
                isFiltered={isFiltered}
                activeTab={activeTab}
                onCreateClick={() => {
                  dispatch(setAdvancedMode({ advancedMode: AgentInputMode.ArtifactMode }));
                  dispatch(setArtifactsViewActive(false));
                  setAgentClickHandler({ agent: activeAgent });
                }}
              />
            )}

            {!isLoading && filteredArtifacts.length > 0 && (
              <ArtifactCardGrid
                artifacts={filteredArtifacts}
                variant={activeTab === ARTIFACTS_TABS.SHARED ? 'shared' : 'my-artifacts'}
                onCardClick={handleCardClick}
                usersById={usersById}
              />
            )}
          </Box>
        </>
      )}
    </Box>
  );
};

export default ArtifactsPage;
